/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * What a settings block shows before its data arrives, or when it never
 * does. The blocks used to render nothing while loading, so a table vanished
 * and a field read as empty; these keep the block's shape and say why.
 */

import { useTranslation } from 'react-i18next';
import { DataStateError, Text } from '@/shared/ui';
import { ExclamationTriangleIcon } from '@/shared/ui/radix-icons';
import { SKELETON_BLOCK, skeletonSlots } from '@/shared/ui/components/basic/data-state/skeletons/skeleton-primitives';
import { BlockMid } from './blocks';

/**
 * Shimmering rows where the block's content will be, plus a line for screen
 * readers and anyone wondering why the block is empty.
 *
 * @param props.rows - How many content rows to suggest.
 * @returns The loading placeholder.
 */
export function BlockLoading({ rows = 4 }: { rows?: number }) {
  const { t } = useTranslation('features/agency-settings');
  return (
    <BlockMid>
      <div aria-busy="true" className="flex flex-col gap-(--space-3)" data-testid="settings-block-loading">
        <Text role="status" style={{ margin: 0, fontSize: 'var(--text-small)', color: 'var(--text-tertiary-soft)' }}>
          {t('v2.items.loading')}
        </Text>
        {skeletonSlots(rows).map((slot) => (
          <div key={slot} className={`${SKELETON_BLOCK} h-4`} style={{ width: `${60 - (slot % 3) * 12}%` }} />
        ))}
      </div>
    </BlockMid>
  );
}

/**
 * The block's content replaced by what went wrong and a way to try again.
 *
 * @param props.onRetry - Refetches the block's data.
 * @returns The error state.
 */
export function BlockError({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation('features/agency-settings');
  return (
    <BlockMid>
      <DataStateError
        icon={(
          <Text component="span" className="flex text-(--status-warning-text)">
            <ExclamationTriangleIcon width={24} height={24} />
          </Text>
        )}
        message={t('v2.items.blockError')}
        retryLabel={t('v2.items.retry')}
        onRetry={onRetry}
        data-testid="settings-block-error"
      />
    </BlockMid>
  );
}
